import interpolateComponents from '@automattic/interpolate-components';
import { differenceInDays, parseISO } from 'date-fns';
import { useMemo } from 'react';

import type { ApiAddress } from 'api/types/ApiAddress';
import type { ApiRemoteOptions } from 'api/types/ApiRemoteOptions';
import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { Box } from 'components/Box/Box';
import { Icon } from 'components/Icon/Icon';
import { IdealistDayLabel } from 'components/IdealistDays/IdealistDayLabel';
import { LegacyTooltip } from 'components/LegacyTooltip/LegacyTooltip';
import { StyledNativeLinkWithRef } from 'components/Link/StyledNativeLinkWithRef';
import type { ApiOrgType } from 'modules/listing/api/org/types/ApiOrgType';
import type { ApiActionType } from 'modules/listing/api/volop/types/ApiActionType';
import { ListingTypeLabel } from 'modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import { ListingViewActionButtons } from 'modules/listing/components/ListingView/ActionButtons/ListingViewActionButtons';
import { useRsvpAnswer } from 'modules/listing/hooks/useRsvpAnswer';
import { getLocationTypeText } from 'utils/listings/getLocationTypeText';

import {
  ListingViewHeaderActionButtonsHolder,
  ListingViewHeaderContent,
  ListingViewHeaderContentHolder,
  ListingViewHeaderHolder,
  ListingViewHeaderLocationDescription,
  ListingViewHeaderOrgName,
  ListingViewHeaderTagHolder,
  ListingViewHeaderTitle,
} from './ListingViewHeader.styled';
import { ListingViewHeaderPublishedTag } from './ListingViewHeaderPublishedTag';
import { getListingViewHeaderLocationSentence } from './getListingViewHeaderLocationSentence';

type Props = {
  actionType?: ApiActionType;
  address: ApiAddress;
  hasAts: boolean;
  hasRsvp?: boolean;
  isPostedAnonymously: boolean;
  isPreview?: boolean;
  listingId: string;
  listingName: string;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  listingUrl: Record<UserLocale, string>;
  listingFirstPublished: ApiISODateTimeString | null;
  orgId?: string;
  orgName?: string;
  orgType?: ApiOrgType;
  orgUrl?: Record<UserLocale, string> | null;
  remoteOptions: ApiRemoteOptions;
  isIdealistDay?: boolean;
  isExpired: boolean;
};

export function ListingViewHeader({
  actionType,
  address,
  hasAts,
  hasRsvp,
  isPostedAnonymously,
  isPreview = false,
  listingId,
  listingName,
  listingType,
  listingUrl,
  listingFirstPublished,
  orgId,
  orgName,
  orgType,
  orgUrl,
  remoteOptions,
  isIdealistDay,
  isExpired,
}: Props) {
  // initialize rsvp buttons if this listing has rsvp enabled
  useRsvpAnswer(listingId, hasRsvp);

  const locationSentence = useMemo(
    () =>
      getListingViewHeaderLocationSentence({
        listingType,
        remoteOptions,
        address,
      }),
    [address, listingType, remoteOptions],
  );

  const isNewListing =
    !isExpired &&
    Boolean(
      listingFirstPublished &&
        differenceInDays(new Date(), parseISO(listingFirstPublished)) < 2,
    );

  return (
    <ListingViewHeaderHolder>
      <ListingTypeLabel
        listingType={listingType}
        orgType={orgType}
        actionType={actionType}
      />

      {listingFirstPublished && !isPreview && (
        <ListingViewHeaderTagHolder $isNew={isNewListing}>
          <ListingViewHeaderPublishedTag
            listingFirstPublished={listingFirstPublished}
            isNew={isNewListing}
          />
        </ListingViewHeaderTagHolder>
      )}

      <ListingViewHeaderContentHolder>
        <ListingViewHeaderContent>
          {isIdealistDay && <IdealistDayLabel />}
          <ListingViewHeaderTitle data-qa-id="listing-name">
            {listingName}
            {isPostedAnonymously && (
              <Box display="inline-block" ml="6px" verticalAlign="super">
                <LegacyTooltip>
                  {getText(
                    `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                  )}
                </LegacyTooltip>
              </Box>
            )}
          </ListingViewHeaderTitle>

          {orgName && (
            <ListingViewHeaderOrgName>
              {orgUrl ? (
                <StyledNativeLinkWithRef
                  href={orgUrl[CURRENT_LOCALE()]}
                  data-qa-id="org-link"
                >
                  {orgName}
                </StyledNativeLinkWithRef>
              ) : (
                orgName
              )}
            </ListingViewHeaderOrgName>
          )}

          <ListingViewHeaderLocationDescription>
            <Icon name="location-filled" size={20} colorName="dustyBlue" />

            <div>
              <span>
                {getLocationTypeText(listingType, remoteOptions.locationType)},
              </span>

              <span>
                {' '}
                {locationSentence &&
                  interpolateComponents({ mixedString: locationSentence })}
              </span>
            </div>
          </ListingViewHeaderLocationDescription>

          {!isExpired && (
            <ListingViewHeaderActionButtonsHolder>
              <ListingViewActionButtons
                hasAts={hasAts}
                hasRsvp={hasRsvp}
                listingId={listingId}
                listingName={listingName}
                listingType={listingType}
                listingUrl={listingUrl}
                orgId={orgId}
                orgName={orgName}
                orgType={orgType}
                mobile={false}
                isPreview={isPreview}
              />
            </ListingViewHeaderActionButtonsHolder>
          )}
        </ListingViewHeaderContent>
      </ListingViewHeaderContentHolder>
    </ListingViewHeaderHolder>
  );
}
